import { Colors } from '@doordash/design-language'
import { get, isEmpty, noop } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import { Redirect } from 'react-router'
import * as actions from '../../../actions'
import i18n from '../../../i18n'
import { requiresLoginSelector } from '../../../selectors'
import Simple from '../../templates/simple'
import { Root } from './styles'
import LayerManager from '@doordash/component-layer-manager'
import { RiskProvider } from '@doordash/component-risk'
import { FeatureFlag, isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import { setW9Signed } from '../../../api'
import { API_PROXY } from '../../../constants'
import { IS_AT_CAPACITY_V2 } from '../../../experimentConstants'
import * as Exp from '../../../experimentConstants'
import { ApplicationStatus, IDVStatusResponse } from '../../../models/types'
import routes from '../../../routes'
import {
  determineCurrentStep,
  stepMatch,
  Steps,
} from '../../../utilities/steps'
import LoadingBox from '../../atoms/loading-box'
import IdentityVerificationAwait from '../../molecules/identity-verification'
import LoginModal from '../../organisms/login-modal'
import Page from '../../molecules/page'
import PageRoot from '../../molecules/page-root'
import PreScreenDetail from '../../molecules/prescreen-detail'
import SignW9 from '../../molecules/sign-w9'
import SignW9V2 from '../../molecules/sign-w9-v2'
import SubmitMailingAddress from '../../molecules/submit-mailing-address'
import SubmitMailingAddressV2 from '../../molecules/submit-mailing-address-v2'
import JapanNonCitizenGuide from '../../molecules/japan-non-citizen-guide'
import ApplicationClosed from '../../organisms/application-closed'
import BackgroundCheck from '../../organisms/background-check'
import Confirmation from '../../organisms/confirmation'
import DropshippingStatus from '../../organisms/dropshipping-status'
import GetSupplies from '../../organisms/get-supplies'
import GetSuppliesV2 from '../../organisms/get-supplies-v2'
import ProfileSetup from '../../organisms/profile-setup'
import ScheduledOrientationPage from '../../organisms/schedule-orientation'
import SignupPhoneVerify from '../../organisms/signup-phone-verify'
import { SimpleStyleType } from '../../templates/simple/constants'
import VehicleDetails from '../../organisms/vehicle-details'
import OnboardingWorkflow from '../onboarding-workflow'
import ProgressionCheck from '../../organisms/progression-check'
import SignUpCompletion from '../sign-up-completion'
import * as events from '../../../events'
import * as DA from '../../../typeModules/events'
import { Action } from 'redux'

declare const window

type OnboardingProps = {
  application?: any
  applicationStatus?: ApplicationStatus
  error?: string
  experiments?: object
  idvStatus?: IDVStatusResponse
  isAtCapacity?: boolean
  isAtCapacityV2?: boolean
  isLoggedIn?: boolean
  isLoadingApplication?: boolean
  mailingAddress?: any
  mailingAddressSubmitted?: boolean
  requiresLogin?: boolean
  scheduledOrientation?: {
    startTime: string
    address: string
  }
  step?: string
  supplyAcquisitionMethod?: string
  uniqueLink: string
  w9Signed?: boolean
  clearErrors?: () => void
  loadApplication?: (any) => Promise<any>
  loadConsumer?: () => Promise<void>
  loadUser?: () => Promise<void>
  loadApplicationWithPersona?: (any) => Promise<any>
  getApplicationStatus?: (uniqueLink: string) => Promise<void>
  setOnboardingStep?: (step: string) => Promise<void>
  trackProgressionCheckRendered?: (
    action: DA.EventActions,
    step: string,
    applicantId: string,
    clientDriven: boolean
  ) => Action
}

export class Onboarding extends React.Component<OnboardingProps> {
  public static defaultProps = {
    isLoggedIn: false,
    isLoadingApplication: false,
    requiresLogin: false,
    loadApplication: noop,
    loadConsumer: noop,
    loadUser: noop,
    loadApplicationWithPersona: noop,
    getApplicationStatus: noop,
    setOnboardingStep: noop,
  }

  public onErrorClose = () => {
    this.props.clearErrors()
  }

  public async componentDidMount() {
    const {
      loadApplication,
      loadConsumer,
      loadUser,
      uniqueLink,
      w9Signed,
      loadApplicationWithPersona,
      getApplicationStatus,
      applicationStatus,
      experiments,
    } = this.props

    // hacky background color change
    const body = document.getElementsByTagName('body')[0]
    const useUserMeEndpoint = get(experiments, Exp.USE_USER_ME_ENDPOINT, false)
    body.style['background-color'] = Colors.PageBackgroundSecondary

    try {
      if (w9Signed) {
        // probably should move this to an action
        await setW9Signed(uniqueLink, true)
      }

      if (useUserMeEndpoint) {
        await loadUser()
      } else {
        await loadConsumer()
      }

      const isIDVEnabled =
        isFeatureFlagEnabled(Exp.IDENTITY_VERIFICATION) ||
        isFeatureFlagEnabled(Exp.IDV_REFERRAL)
      const isJPIDVEnabled = isFeatureFlagEnabled(Exp.JAPAN_DX_ONBOARDING_V2)

      if (isIDVEnabled || isJPIDVEnabled) {
        await loadApplicationWithPersona(uniqueLink)
      } else {
        await loadApplication(uniqueLink)
      }
      if (!get(applicationStatus, 'hasLoaded', false)) {
        await getApplicationStatus(uniqueLink)
      }
    } catch (e) {
      // noop
    }
  }

  private router = (currentStep) => {
    const {
      application,
      scheduledOrientation,
      step,
      uniqueLink,
      idvStatus,
      trackProgressionCheckRendered,
    } = this.props

    /*
      I had to do this .replace() because we're building the path with a an
      optional route :param? so we get a trailing ?
      I think this is a bug in lib-routing
    */
    const filteredStep = step && step.length ? step.replace('?', '') : step
    const { path } = stepMatch(filteredStep)
    const nextStep =
      filteredStep !== path
        ? path
        : path !== currentStep.path
        ? currentStep.path
        : path
    if (nextStep !== filteredStep) {
      const dasherflow = routes.dasherflow.buildPath({
        uniqueLink,
        step: nextStep,
      })
      return <Redirect to={{ pathname: dasherflow }} />
    }

    switch (currentStep.path) {
      case Steps.StartOnBoardingWorkflow:
        return <OnboardingWorkflow />
      case Steps.BackgroundCheckAwait:
        return (
          <PreScreenDetail
            application={application}
            virtual={application.use_virtual_orientation}
          />
        )
      case Steps.ApplicationClosed:
        return <ApplicationClosed />
      case Steps.BackgroundCheck:
        return (
          <BackgroundCheck
            uniqueLink={uniqueLink}
            country={application.country}
          />
        )
      case Steps.VehicleDetails:
        return <VehicleDetails uniqueLink={uniqueLink} />
      case Steps.SignUpCompletion:
        return <SignUpCompletion />
      case Steps.PhoneVerification:
        return <SignupPhoneVerify uniqueLink={uniqueLink} />
      case Steps.ProfileSetup:
        return (
          <ProfileSetup
            shouldShowOfferLetter={get(application, 'isWEligible')}
            uniqueLink={uniqueLink}
          />
        )
      case Steps.Orientation:
      case Steps.Reschedule:
        return (
          <ScheduledOrientationPage
            application={application}
            uniqueLink={uniqueLink}
          />
        )
      case Steps.GetSupplies:
        return <GetSupplies uniqueLink={uniqueLink} />
      case Steps.GetSuppliesV2:
        return <GetSuppliesV2 uniqueLink={uniqueLink} />
      case Steps.SignW9V2:
        return <SignW9V2 uniqueLink={uniqueLink} />
      case Steps.SignW9:
        return <SignW9 uniqueLink={uniqueLink} />
      case Steps.MailingAddress:
        return <SubmitMailingAddress uniqueLink={uniqueLink} />
      case Steps.MailingAddressV2:
        return <SubmitMailingAddressV2 uniqueLink={uniqueLink} />
      case Steps.DropshippingConfirm:
        return (
          // Bank onboarding experiment should be
          // paired with dropshipping status
          <FeatureFlag
            flagName="bank_onboarding"
            on={() => (
              <DropshippingStatus
                uniqueLink={uniqueLink}
                application={application}
              />
            )}
            off={() => (
              <FeatureFlag
                flagName="dropshipping_status"
                on={() => (
                  <DropshippingStatus
                    uniqueLink={uniqueLink}
                    application={application}
                  />
                )}
                off={() => (
                  <Confirmation
                    status={application.backgroundCheckResults}
                    mailingAddress={application.mailingAddress}
                  />
                )}
              />
            )}
          />
        )
      case Steps.IdentityVerificationAwait:
        return <IdentityVerificationAwait />
      case Steps.ProgressionCheck:
        return (
          <ProgressionCheck
            application={application}
            scheduledOrientation={scheduledOrientation}
            uniqueLink={uniqueLink}
            idvStatus={idvStatus}
            trackProgressionCheckRendered={trackProgressionCheckRendered}
          />
        )
      case Steps.JapanNonCitizenGuide:
        return <JapanNonCitizenGuide />
      default:
        return <div>404 not found</div> // @TODO: 404?
    }
  }

  // tslint:disable-next-line:member-ordering
  public render() {
    const {
      application,
      isLoggedIn,
      isLoadingApplication,
      requiresLogin,
      uniqueLink,
      error,
      experiments,
      supplyAcquisitionMethod,
      mailingAddressSubmitted,
      isAtCapacity,
      isAtCapacityV2,
      idvStatus,
      applicationStatus,
      setOnboardingStep,
      step,
    } = this.props
    const currentStep = determineCurrentStep(
      application,
      experiments,
      uniqueLink,
      {
        supplyAcquisitionMethod,
        mailingAddressSubmitted,
        isAtCapacity: isAtCapacity || isAtCapacityV2,
        idvStatus,
        hasCompletedFirstDelivery: get(
          applicationStatus,
          'onboardingProgress.hasCompletedFirstDelivery'
        ),
        step,
      }
    )

    if (currentStep && currentStep.path) {
      setOnboardingStep(currentStep.path)
    }

    const applicationLoaded = !!application
    const isLoggedInIsSet = isLoggedIn !== null

    if (
      currentStep === false ||
      !applicationLoaded ||
      !isLoggedInIsSet ||
      isLoadingApplication
    ) {
      return <LoadingBox />
    }

    if (requiresLogin) {
      // If IDS experiment enabled, take user to IDS login webview
      // Otherwise use legacy Login Modal
      /**
       * needs to login if:
       * 1) hasConsumerAccount
       * 2) application is empty
       * 3) needsBgCheckInfo in application is false, which means they don't
       *    have a Dasher account yet
       */
      if (
        (get(application, 'hasConsumerAccount', false) ||
          isEmpty(application) ||
          !get(application, 'needsBgCheckInfo', null)) &&
        isFeatureFlagEnabled(Exp.DASHER_WEB_IDENTITY_SERVICE_MIGRATION)
      ) {
        window.location.assign(routes.login.path)
        return <React.Fragment />
      }

      return (
        <LoginModal
          uniqueLink={uniqueLink}
          email={application.email}
          currentStep={currentStep.path}
        />
      )
    }

    const baseUrl = window.view.API_URL ? new URL(window.view.API_URL) : null

    return (
      <Simple
        includeMobileHeader={currentStep.includeMobileHeader}
        includeFooter={false}
        headerIsDark={false}
        clearErrors={this.onErrorClose}
        errorMessage={error}
        type={SimpleStyleType.Standard}
      >
        <RiskProvider
          language={i18n.getCurrentLocale()}
          proxyOverride={{
            production: {
              baseUrl: baseUrl ? `${baseUrl.protocol}//${baseUrl.host}` : '',
              apiProxy: API_PROXY,
            },
            local: {
              baseUrl: baseUrl ? `${baseUrl.protocol}//${baseUrl.host}` : '',
              apiProxy: 'bffLocal',
            },
          }}
        >
          <LayerManager>
            <Root includeMobileHeader={currentStep.includeMobileHeader}>
              <Page>
                <PageRoot>{this.router(currentStep)}</PageRoot>
              </Page>
            </Root>
          </LayerManager>
        </RiskProvider>
      </Simple>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    step: ownProps.step,
    application: state.application,
    applicationStatus: state.applicationStatus.data,
    error: state.errorMessage,
    experiments: state.experiments,
    idvStatus: state.idvStatus,
    isAtCapacity: state.isAtCapacity,
    isAtCapacityV2: state.experiments[IS_AT_CAPACITY_V2],
    isLoggedIn: state.isLoggedIn,
    isLoadingApplication: state.isLoadingApplication,
    mailingAddress: state.mailingAddress,
    mailingAddressSubmitted: state.mailingAddress.isSubmitted,
    requiresLogin: requiresLoginSelector(state),
    scheduledOrientation: state.scheduledOrientation,
    supplyAcquisitionMethod: state.supplyAcquisitionMethod,
  }
}

const mapDispatchToProps = {
  clearErrors: actions.clearErrors,
  getApplicationStatus: actions.getApplicationStatus,
  loadApplication: actions.loadApplication,
  loadApplicationWithPersona: actions.loadApplicationWithPersona,
  loadConsumer: actions.loadConsumer,
  loadUser: actions.loadUser,
  setOnboardingStep: actions.setOnboardingStep,
  trackProgressionCheckRendered: events.trackProgressionCheckRendered,
}

export default connect(mapStateToProps, mapDispatchToProps)(Onboarding)
